import { isAdmin } from '@/constants/roles';
import { isEmbeddedSidebar, isInIframe } from '@/lib/embedded-sidebar';
import { cn } from '@/lib/utils';
import capitalize from 'lodash/capitalize';
import {
    Ellipsis,
    LogOut,
    Moon,
    ShieldUser,
    Trash2
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import DMOrganizationSwitcher from '@/components/LeftSidebar/DMSidebarFooter/DMOrganizationSwitcher';
import { useTheme } from '@/components/ThemeProvider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem
} from '@/components/ui/sidebar';
import { Switch } from '@/components/ui/switch';
import { Translator } from 'components/i18n';

import { useAuth } from '@/hooks/useAuth';

import { DMDeleteHistoryDialog } from './DMDeleteHistoryDialog';

interface DMSidebarUserMenuProps {
    isCollapsed?: boolean;
}

export default function DMSidebarUserMenu({
    isCollapsed = false
}: DMSidebarUserMenuProps) {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { theme, setTheme } = useTheme();

    const [menuOpen, setMenuOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const userIsAdmin = useMemo(() => {
        return isAdmin(user?.metadata?.organization_roles);
    }, [user]);

    // Skjul org-switcheren når appen kører embedded (query-param) eller i en iframe.
    const isEmbedded = isEmbeddedSidebar || isInIframe;
    const showOrgSwitcher = !isEmbedded;
    const showUserHeader = !isEmbedded;

    if (!user) return null;

    const currentOrgId = user?.metadata?.organization_id ?? null;
    const currentOrgValue = user?.metadata?.organization ?? null;

    const displayName =
        (user as any)?.display_name || user.identifier || 'User';
    const secondaryLine = user.identifier?.includes('@')
        ? user.identifier
        : currentOrgValue;

    return (
        <>
            <SidebarMenu>
                <SidebarMenuItem>
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                        <DropdownMenuTrigger asChild>
                            <SidebarMenuButton
                                size="lg"
                                className={cn(
                                    'flex data-[state=open]:bg-sidebar-accent rounded-none transition-all duration-200 data-[state=open]:text-sidebar-accent-foreground',
                                    isCollapsed
                                        ? 'justify-center p-2 py-4'
                                        : 'gap-4 p-4 py-8'
                                )}
                            >
                                {!isCollapsed && (
                                    <p className="text-base">General</p>
                                )}
                                <Ellipsis
                                    className={cn(
                                        'size-4! text-muted-foreground shrink-0',
                                        !isCollapsed && 'ml-auto'
                                    )}
                                />
                            </SidebarMenuButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            side="top"
                            align="center"
                            className="bg-background min-w-[max(16rem,calc(var(--radix-dropdown-menu-trigger-width)-1.5rem))]"
                        >
                            {showUserHeader && (
                                <>
                                    <DropdownMenuLabel className="flex items-center gap-3 px-2.5 py-2.5 font-normal">
                                        <Avatar className="size-10 shrink-0">
                                            <AvatarImage
                                                src={user.metadata?.image}
                                                alt=""
                                            />
                                            <AvatarFallback className="bg-foreground font-semibold text-background">
                                                {capitalize(displayName[0])}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0">
                                            <p className="font-medium text-foreground text-[length:var(--fs-title-compact)] leading-[var(--lh-title-compact)] truncate">
                                                {displayName}
                                            </p>
                                            {secondaryLine && (
                                                <p className="text-body text-muted-foreground truncate">
                                                    {secondaryLine}
                                                </p>
                                            )}
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                </>
                            )}

                            {userIsAdmin && (
                                <DropdownMenuItem
                                    onClick={() =>
                                        navigate('/admin', { replace: true })
                                    }
                                >
                                    <ShieldUser />
                                    <span>Admin</span>
                                </DropdownMenuItem>
                            )}

                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onSelect={(event) => {
                                    event.preventDefault();
                                    setTheme(
                                        theme === 'dark' ? 'light' : 'dark'
                                    );
                                }}
                            >
                                <Moon />
                                <span>Dark Mode</span>
                                <Switch
                                    size="sm"
                                    className="ml-auto pointer-events-none"
                                    checked={theme === 'dark'}
                                    tabIndex={-1}
                                    aria-label="Toggle dark mode"
                                />
                            </DropdownMenuItem>

                            {showOrgSwitcher && (
                                <DMOrganizationSwitcher
                                    enabled={menuOpen}
                                    currentOrgId={currentOrgId}
                                    currentOrgValue={currentOrgValue}
                                />
                            )}

                            <DropdownMenuSeparator />

                            <DropdownMenuItem
                                variant="destructive"
                                onClick={() => setDeleteDialogOpen(true)}
                            >
                                <Trash2 />
                                <span>
                                    {/* <Translator path="chat.settings.actions.delete.button" /> */}
                                    Delete History
                                </span>
                            </DropdownMenuItem>

                            <DropdownMenuItem onClick={() => logout(true)}>
                                <LogOut />
                                <span>
                                    <Translator path="navigation.user.menu.logout" />
                                </span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </SidebarMenuItem>
            </SidebarMenu>

            <DMDeleteHistoryDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
            />
        </>
    );
}
